import {
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectionStrategy,
  Component,
  OnChanges,
  OnInit,
  SimpleChanges,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { translate } from '@jsverse/transloco';
import { filter, tap } from 'rxjs';
import { TabObject } from '@inmoment/the-kitchen';

import { AppSharedModule } from 'app/app-shared.module';
import { AiInsightsDataprepService } from 'app/providers/storage-api/ai-insights-dataprep.service';
import { ItemLabel } from 'app/providers/storage-api/object-label.service';
import { AlertService } from 'app/services/alert.service';
import { WidgetDataSource } from '../../../../../models';
import {
  Widget,
  WidgetGroup,
  Dashboard,
} from '../../../../../providers/bridge-api/dashboard.service';
import {
  AiInsight,
  AiInsightsService,
} from '../../../../../providers/semantria-api/ai-insights.service';
import {
  MatchmakerHandler,
  MatchmakerHandlerParameter,
} from '../../../../../providers/semantria-api/spote.service';
import {
  EnableClassTelemetry,
  EnableComponentTelemetry,
  injectComponentTelemetryService,
} from '../../../../../services/otel.service';
import { WidgetAiInsightsService } from '../../../services/widget-ai-insights.service';
import { WidgetChartOptions } from '../../widget-chart/widget-chart.component';
import { InsightCardComponent } from './insight-card/insight-card.component';
import { WidgetAiInsightsPointSelectionService } from './services/point-selection/widget-ai-insights-point-selection.service';
import {
  MatchmakerHandlerValidationResult,
  NumericRange,
} from './services/validation/widget-ai-insights-validator.service';

export interface WidgetAiInsightsOptions {
  validatedScenes: MatchmakerHandlerValidationResult[];
  dataPointCount: (handler: MatchmakerHandler) => number;
  widget: Widget<WidgetChartOptions>;
  dashboard: Dashboard;
  dataSource: WidgetDataSource;
  widgetGroup?: WidgetGroup;
  isReadOnly: boolean;
  contentChannel?: string;
  canGenerateInsights: boolean;
  currentUserId?: string;
}

interface MatchmakerSceneModel {
  id: string;
  name: string;
  shortPurpose: string;
  longPurpose: string;
  nickname: string;
  parameters: MatchmakerHandlerParameter[];
  disabled?: boolean;
  tooltip?: string;
}

@EnableClassTelemetry()
@Component({
  selector: 'app-widget-ai-insights',
  imports: [AppSharedModule, InsightCardComponent],
  providers: [WidgetAiInsightsPointSelectionService],
  templateUrl: './widget-ai-insights.component.html',
  styleUrl: './widget-ai-insights.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class WidgetAiInsightsComponent implements OnInit, OnChanges {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();

  options = input<WidgetAiInsightsOptions>();
  selectedPoints = input<any[]>([]);

  choosePoints = output<MatchmakerHandler>();
  autoChoosePoints = output<MatchmakerHandler>();
  resetPoints = output();
  insightCreated = output<Partial<AiInsight>>();

  private pointSelectionService = inject(WidgetAiInsightsPointSelectionService);
  private widgetAiInsightsService = inject(WidgetAiInsightsService);
  private aiInsightsDataprepService = inject(AiInsightsDataprepService);
  private aiInsightsService = inject(AiInsightsService);
  private alertService = inject(AlertService);

  tabs: TabObject[] = [];
  activeTab = signal<'generate' | 'list'>('generate');

  availableScenes: MatchmakerSceneModel[];
  selectedScene: MatchmakerSceneModel;
  selectedSceneHandler: MatchmakerHandler;

  isInsightsLoading = signal(true);

  isPointSelectionFlow = signal(false);
  isSelectionValid = signal(false);
  isZeroPointSelection = signal(false);
  clickablePoints: NumericRange;
  remainingPointsMin = -1;
  remainingPointsMax = 0;

  readonly showGenerateTab = signal(false);

  constructor() {
    this.widgetAiInsightsService.aiInsights$
      .pipe(
        takeUntilDestroyed(),
        filter((res) => !!res),
        tap(() => this.isInsightsLoading.set(false))
      )
      .subscribe((res) => {
        if (res?.length && !this.isPointSelectionFlow() && this.activeTab() !== 'list') {
          this.activeTab.set('list');
        }
      });
  }

  ngOnInit(): void {
    this.configureTabs();

    this.initScenes();

    this.widgetAiInsightsService.loadInsights({
      widgetId: this.options().widget.id,
      projectId: this.options().dashboard.project_id,
      dashboardId: this.options().dashboard.id,
    });

    this.widgetAiInsightsService.loadInsightTags({
      projectId: this.options().dashboard.project_id,
    });

    this.widgetAiInsightsService.subscribeToRtr(
      this.options().dashboard.project_id,
      this.options().widget.id
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.options) {
      this.configureTabs();
    }

    if (changes.selectedPoints?.currentValue && this.selectedSceneHandler) {
      if (this.isZeroPointSelection() && this.selectedPoints().length) {
        setTimeout(() => this.onGenerateInsight());
        return;
      }

      this.remainingPointsMin = this.clickablePoints.min - this.selectedPoints().length;
      this.remainingPointsMax = this.clickablePoints.max - this.selectedPoints().length;
      if (!this.clickablePoints.max || this.clickablePoints.max === Number.MAX_SAFE_INTEGER) {
        this.remainingPointsMax = 0;
      }

      this.isSelectionValid.set(
        this.pointSelectionService.checkSelectionValidForHandler(
          this.selectedPoints(),
          this.selectedSceneHandler,
          this.options().widget
        )
      );
    }
  }

  private initScenes() {
    this.availableScenes = this.options().validatedScenes.map((scene) => ({
      id: scene.handler.handler_id,
      name: scene.handler.handler_name,
      shortPurpose:
        scene.handler.properties.find((p) => p.property_key === 'SHORT_PURPOSE')?.property_value ??
        scene.handler.handler_name,
      longPurpose: scene.handler.properties.find((p) => p.property_key === 'LONG_PURPOSE')
        ?.property_value,
      nickname: scene.handler.properties.find((p) => p.property_key === 'NICKNAME')?.property_value,
      parameters: scene.handler.parameters,
      disabled: scene.disabled,
      tooltip: scene.reason,
    }));

    const firstValidScene = this.availableScenes.find((scene) => !scene.disabled);
    this.onSceneSelected({ detail: { value: firstValidScene } });
  }

  onSceneSelected($event) {
    if (this.selectedScene === $event.detail.value) {
      return;
    }

    this.selectedScene = $event.detail.value;
    this.selectedSceneHandler = this.options().validatedScenes.find(
      (s) => s.handler.handler_id === this.selectedScene?.id
    )?.handler;
    this.clickablePoints = this.pointSelectionService.getClickablePoints(this.selectedSceneHandler);
    this.isZeroPointSelection.set(
      this.clickablePoints.min === 0 ||
        this.clickablePoints.min === this.options().dataPointCount(this.selectedSceneHandler)
    );
    this.isSelectionValid.set(this.isZeroPointSelection());
  }

  onChoosePoints() {
    this.choosePoints.emit(this.selectedSceneHandler);

    this.isPointSelectionFlow.set(true);
  }

  onCancel() {
    this.resetPoints.emit();

    this.isPointSelectionFlow.set(false);
  }

  async onGenerateInsight() {
    if (this.isZeroPointSelection() && !this.selectedPoints().length) {
      this.autoChoosePoints.emit(this.selectedSceneHandler);
      return;
    }

    const parameters = this.pointSelectionService.assembleHandlerParameters(
      this.selectedPoints(),
      this.selectedSceneHandler,
      this.options().widget,
      this.options().dataSource,
      this.options().contentChannel,
      this.options().widgetGroup
    );

    parameters.properties = {
      longPurpose: this.selectedScene.longPurpose,
      shortPurpose: this.selectedScene.shortPurpose,
      widgetId: this.options().widget.id,
      dashboardId: this.options().dashboard.id,
      projectId: this.options().dashboard.project_id,
      sceneId: this.selectedScene.id,
      analysisId: this.options().dataSource.id,
      ...parameters.properties,
    };

    const insight: Partial<AiInsight> = {
      insight_id: null,
      properties: parameters.properties,
      insight_status: 'QUEUED',
      creation_date: new Date().toISOString(),
      creator_user_id: this.options().currentUserId,
    };
    this.activeTab.set('list');

    this.resetPoints.emit();

    this.isPointSelectionFlow.set(false);

    this.widgetAiInsightsService.storeInsight(insight);

    this.aiInsightsDataprepService
      .generateInsight(
        this.options().dashboard.project_id,
        this.options().dataSource,
        this.selectedScene.id,
        parameters
      )
      .then((res) => {
        insight.insight_id = res.insightId;
        insight.insight_status = 'IN_PROGRESS';
        if (res.errorMessage?.trim()) {
          insight.insight_status = 'FAILED';
          insight.error_message = res.errorMessage;
        }

        this.insightCreated.emit(insight);
        this.widgetAiInsightsService.storeInsight(insight);
      })
      .catch((error) => {
        this.alertService.showErrorToast(
          translate('widgets.ai-insights.could-not-generate-insight'),
          error
        );
        this.widgetAiInsightsService.removeInsight(insight.insight_id);
      });
  }

  onInsightDeleted(insight: AiInsight) {
    this.aiInsightsService.deleteInsight(insight.insight_id).then(() => {
      this.widgetAiInsightsService.removeInsight(insight.insight_id);
    });
  }

  onRegenerateInsight(insight: AiInsight) {
    if (this.options().isReadOnly) {
      return;
    }

    this.aiInsightsDataprepService.regenerateInsight(insight.insight_id).then(() => {
      insight.creation_date = new Date().toISOString();
      this.widgetAiInsightsService.storeInsight(insight);
    });
  }

  async onPublishInsight($event: { insight: AiInsight; labels: ItemLabel[] }) {
    if (!$event?.insight) {
      return;
    }

    const insight = $event.insight;
    const labels = $event.labels ?? [];

    const tags: string[] = [];
    const tagSet = new Set<string>();
    for (const label of labels) {
      const tagLabel = label.label?.trim();
      if (tagLabel && !tagSet.has(tagLabel)) {
        tagSet.add(tagLabel);
        this.widgetAiInsightsService.storeInsightTag(tagLabel);
        tags.push(tagLabel);
      }
    }

    const params: Partial<AiInsight> = {
      properties: {
        ...insight.properties,
        shortPurpose: insight.properties.shortPurpose,
        includeImage: insight.properties.includeImage,
      },
      comment: insight.comment,
      published: true,
      tags,
    };
    try {
      const updateInsight = this.aiInsightsService.updateInsight(insight.insight_id, params);
      const updateLocalization = this.aiInsightsService
        .deleteInsightLocalization(insight.insight_id, insight.localizations[0].language)
        .then(() => {
          this.aiInsightsService.addInsightLocalization(insight.insight_id, {
            text: insight.localizations[0].text,
            language: insight.localizations[0].language,
          });
        });
      await Promise.all([updateInsight, updateLocalization]);

      // Ensure UI retains updated values immediately.
      this.widgetAiInsightsService.storeInsight({
        ...insight,
        properties: {
          ...insight.properties,
          shortPurpose: insight.properties.shortPurpose,
          includeImage: insight.properties.includeImage,
        },
        localizations: insight.localizations,
        published: true,
        comment: insight.comment,
        tags,
        publisher_user_id: this.options().currentUserId,
        modification_date: new Date().toISOString(),
      });
    } catch (e) {
      this.alertService.showErrorToast(
        translate('widgets.ai-insights.could-not-publish-insight'),
        e
      );
    }
  }

  private configureTabs() {
    const canGenerate = this.options().canGenerateInsights;
    this.showGenerateTab.set(canGenerate);

    this.tabs = [
      {
        id: 'list',
        title: translate('widgets.ai-insights.list'),
      },
    ];

    if (canGenerate) {
      this.tabs.unshift({
        id: 'generate',
        title: translate('widgets.ai-insights.generate'),
      });
    }

    const availableTabIds = this.tabs.map((tab) => tab.id);
    const currentTab = this.activeTab();

    if (!availableTabIds.includes(currentTab)) {
      this.activeTab.set(this.tabs[0].id as 'generate' | 'list');
    }
  }
}
